import type { MeResponse } from '@tabel/shared/schemas/auth';
import type { CalcResponse, Cell, CellBase } from '@tabel/shared/schemas/cells';
import type { EngineDayCalc } from '@tabel/shared/schemas/day-trace';
import type {
  AnswerRequest,
  AnswerResponse,
  PreviewResponse,
  QuestionsResponse,
  QuestionsSummary,
  ReviewRequest,
  ReviewResponse,
  TimesheetSummary,
} from '@tabel/shared/schemas/questions';
import type {
  DayRequest,
  DayResponse,
  DraftResponse,
  ExampleDay,
  GoldenView,
  Impact,
  ImpactRequest,
  ImpactResponse,
  PinGoldenRequest,
  PublishRequest,
  PublishResponse,
  RuleRequestView,
  PreviewResponse as RulesPreviewResponse,
  RulesView,
  SaveBlocksRequest,
  SaveBlocksResponse,
  SetRuleRequestStatus,
  VersionView,
} from '@tabel/shared/schemas/rules';
import { PlanListResponse, PlanResponse } from '@tabel/shared/schemas/rules-plan';
import type {
  Candidate,
  CopyRowsPreview,
  CopyRowsResponse,
  CreateTimesheetPreview,
  CreateTimesheetRequest,
  Status,
  Timesheet,
  TimesheetListItem,
  TimesheetOverlap,
} from '@tabel/shared/schemas/timesheets';
import type { UndoResponse } from '@tabel/shared/schemas/undo';
import { ApiError } from './errors';
import { type AppRole, contextHeaders } from './role';

/*
 * Путь бригадира (003, US3; contracts/http-api.md, «Изменённые маршруты», `POST /api/actions/:id/undo`). Поля для
 * «Отменить» есть только у табеля на движке: без них тост — без «Отменить».
 */

/** Запись журнала для «Отменить» в тосте (R9): ответ, пачка, правка, отмена правки, добавление, копирование, удаление. */
export interface Undoable {
  undoActionId?: number | null;
}

/** Бригада бригадира на объекте (`/api/me`, окно «Новый табель»). */
export type Brigade = NonNullable<MeResponse['brigades']>[number];

/** Новый табель: бригада (если у бригадира несколько), даты; пересечение — только с `confirmOverlap`. */
export type CreateBody = Omit<CreateTimesheetRequest, 'dryRun'>;

export type { CopyRowsPreview, TimesheetOverlap, UndoResponse };

/** Пересечения из `details` ответа `409 PERIOD_OVERLAP`. */
export function overlapsOf(details: unknown): TimesheetOverlap[] {
  const list = (details as { overlaps?: unknown } | null | undefined)?.overlaps;
  return Array.isArray(list) ? (list as TimesheetOverlap[]) : [];
}

/** `undoActionId` ответа, если сервер его прислал. */
export function undoIdOf(res: unknown): number | null {
  const v = (res as Undoable | null | undefined)?.undoActionId;
  return typeof v === 'number' && Number.isInteger(v) && v > 0 ? v : null;
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      credentials: 'include',
      headers: {
        ...contextHeaders(), // X-Role и X-Object — в каждом запросе, включая чтение
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(0, 'NETWORK', 'Нет связи с сервером', null);
  }
  const requestId = res.headers.get('x-request-id');
  if (res.status === 204) return undefined as T;
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    const e = data?.error;
    if (res.status === 401 && e?.code === 'UNAUTHENTICATED' && !location.pathname.startsWith('/login')) {
      location.assign('/login');
    }
    throw new ApiError(
      res.status,
      e?.code ?? 'INTERNAL',
      e?.message ?? `Ошибка ${res.status}`,
      e?.requestId ?? requestId,
      e?.details?.retryAfterSec, // `RATE_LIMITED`, `LOGIN_LOCKED` — в `details` (Р-91)
      e?.details,
    );
  }
  return (await res.json()) as T;
}

/**
 * Состояние дня, с которым открыта форма правки (003, T025): сервер сверяет его с текущим, не совпало —
 * `409 STALE_STATE` с текущей ячейкой в `details.current`.
 */
export type { CellBase };

const cellUrl = (id: number, personId: string, date: string) =>
  `/api/timesheets/${id}/cells/${encodeURIComponent(personId)}/${date}`;

/** Фильтр списка вопросов табеля (`GET …/questions`): без фильтра — все вопросы табеля. */
export interface QuestionsFilter {
  state?: 'open' | 'done' | 'all';
  date?: string;
  personId?: string;
}

function query(f: QuestionsFilter | undefined): string {
  const p = new URLSearchParams();
  for (const k of ['state', 'date', 'personId'] as const) {
    const v = f?.[k];
    if (v) p.set(k, v);
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

export const api = {
  login: (login: string, password: string) =>
    request<{ user: unknown }>('POST', '/api/auth/login', { login, password }),
  logout: () => request<void>('POST', '/api/auth/logout'),
  me: () => request<MeResponse>('GET', '/api/me'),
  health: () => request<{ status: string; version: string; lastPassAt: string | null }>('GET', '/api/health'),
  list: () => request<TimesheetListItem[]>('GET', '/api/timesheets'),
  create: (b: CreateBody) => request<TimesheetListItem>('POST', '/api/timesheets', b),
  /** Проверка нового табеля без создания (`dryRun`): табели той же бригады с пересечением дат. */
  createCheck: (b: CreateBody) =>
    request<CreateTimesheetPreview>('POST', '/api/timesheets', { ...b, dryRun: true }),
  get: (id: number) => request<Timesheet>('GET', `/api/timesheets/${id}`),
  candidates: (id: number, q: string) =>
    request<Candidate[]>('GET', `/api/timesheets/${id}/candidates?q=${encodeURIComponent(q)}`),
  addRows: (id: number, personIds: string[]) =>
    request<Timesheet & Undoable>('POST', `/api/timesheets/${id}/rows`, { personIds }),
  copyRows: (id: number, sourceId: number) =>
    request<CopyRowsResponse>('POST', `/api/timesheets/${id}/rows/copy`, { sourceId }),
  /** Предпросмотр копирования (`dryRun`): ничего не добавляет. */
  copyPreview: (id: number, sourceId: number) =>
    request<CopyRowsPreview>('POST', `/api/timesheets/${id}/rows/copy`, { sourceId, dryRun: true }),
  /** Убрать человека: табель, `undoActionId` и, у табеля на движке, сколько вопросов снято. */
  removeRow: (id: number, personId: string) =>
    request<Timesheet & Undoable & { removedQuestions?: number }>(
      'DELETE',
      `/api/timesheets/${id}/rows/${encodeURIComponent(personId)}`,
    ),
  calc: (id: number, personId: string, date: string) =>
    request<CalcResponse>('GET', `${cellUrl(id, personId, date)}/calc`),
  edit: (id: number, personId: string, date: string, hours: number, comment: string, base: CellBase) =>
    request<Cell & Undoable>('PUT', `${cellUrl(id, personId, date)}/edit`, { hours, comment, base }),
  cancelEdit: (id: number, personId: string, date: string, base: CellBase) =>
    request<Cell & Undoable>('DELETE', `${cellUrl(id, personId, date)}/edit`, { base }),
  lunch: (id: number, personId: string, date: string, value: boolean) =>
    request<Cell>('PUT', `${cellUrl(id, personId, date)}/lunch`, { value }),
  absence: (id: number, personId: string, date: string, code: string | null) =>
    request<Cell>('PUT', `${cellUrl(id, personId, date)}/absence`, { code }),
  /** Удалить черновик (FR-035): `204` без тела. */
  deleteTs: (id: number) => request<void>('DELETE', `/api/timesheets/${id}`),
  /**
   * «Завершить» (003, R10): есть вопросы без ответа — сервер отвечает `409 QUESTIONS_OPEN`, пока не прислано
   * `withOpenQuestions: true` (правило «разрешить с подтверждением»); при правиле «запретить» — всегда.
   */
  complete: (id: number, withOpenQuestions = false) =>
    request<{ status: Status }>('POST', `/api/timesheets/${id}/complete`, { withOpenQuestions }),
  /** Сводка перед «Завершить»: люди, часы, правки, вопросы без ответа (первые 5), на объекте сегодня. */
  summary: (id: number) => request<TimesheetSummary>('GET', `/api/timesheets/${id}/summary`),
  /** Вопросы бригадиру по табелю (003): список, счётчики и параметры объекта. */
  questions: (id: number, f?: QuestionsFilter) =>
    request<QuestionsResponse>('GET', `/api/timesheets/${id}/questions${query(f)}`),
  /** «В табель попадёт»: расчёт ответа без записи (R6). Ошибки ввода — в `results[].error`. */
  previewAnswer: (id: number, body: AnswerRequest) =>
    request<PreviewResponse>('POST', `/api/timesheets/${id}/questions/preview`, body),
  /** Записать ответ на один или несколько вопросов одного типа. Табель интерфейс перечитывает сам. */
  answer: (id: number, body: AnswerRequest) =>
    request<AnswerResponse>('POST', `/api/timesheets/${id}/questions/answer`, body),
  /** Решение кадров по ответу: «Принято» или «Уточнить» с комментарием. */
  review: (id: number, questionId: number, body: ReviewRequest) =>
    request<ReviewResponse>('POST', `/api/timesheets/${id}/questions/${questionId}/review`, body),
  /** Сводка при входе (бригадир): по табелям в работе — новых за вчера и давно без ответа, без ФИО. */
  questionsSummary: () => request<QuestionsSummary>('GET', '/api/questions/summary'),
  returnTs: (id: number, comment: string) =>
    request<{ status: Status }>('POST', `/api/timesheets/${id}/return`, { comment }),
  sign: (id: number) => request<{ status: Status }>('POST', `/api/timesheets/${id}/sign`, { confirm: true }),
  /**
   * «Отменить» в тосте (R9): обратное действие к своему последнему действию в табеле. Ошибки — `409 UNDO_NOT_LAST`,
   * `UNDO_STALE`, `UNDO_EXPIRED`, `403 FORBIDDEN`.
   */
  undo: (actionId: number) => request<UndoResponse>('POST', `/api/actions/${actionId}/undo`),
  /**
   * «Правила расчёта» (004, contracts/http-api.md, «Чтение»): правила на дату (по умолчанию — сегодня); `from`/`to` —
   * период табеля, тогда в ответе версии периода (`periodVersions`).
   */
  rules: (q: RulesQuery = {}) =>
    request<RulesView>('GET', `/api/rules${search({ date: q.date, from: q.from, to: q.to })}`),
  /** Пример дня правила в области роли; дней нет — `{ none: true, reason: 'no_days' }`. */
  rulesExample: (group: string, date?: string) =>
    request<ExampleDay>('GET', `/api/rules/example${search({ group, date })}`),
  /** День «Сейчас» и, с кандидатом, «После публикации». */
  rulesDay: (body: DayRequest) => request<DayResponse>('POST', '/api/rules/day', body),
  /** Расчёт дня для правой панели «Открыть расчёт дня» — тот же ответ, что в карточке дня (research R6). */
  rulesDayExplain: (personId: string, date: string) =>
    request<EngineDayCalc>('GET', `/api/rules/day-explain${search({ personId, date })}`),
  /** Люди для «Выбрать день» в области роли: 2–50 символов, до 20 человек. */
  rulesPeople: (query: string) => request<RulePerson[]>('GET', `/api/rules/people${search({ query })}`),
  /** «История изменений»: версии, сверху новые; `from`/`to` — даты публикации. */
  rulesHistory: (p: { from?: string; to?: string } = {}) =>
    request<VersionView[]>('GET', `/api/rules/history${search(p)}`),
  /** Вопросы реестра расчёта для «Вопрос реестра». */
  rulesRegistry: () => request<RegistryQuestion[]>('GET', '/api/rules/registry'),
  /*
   * Черновик и влияние (004, US2, T062; contracts/http-api.md, «Черновик и влияние»). Черновик на объекте один, его
   * меняет только ведущий; `etag` защищает от записи поверх чужих изменений (`409 DRAFT_CHANGED`).
   */
  /** «Сохранить в черновик»: блоки одного правила целиком; без черновика — без `etag`, черновик появится. */
  saveBlocks: (body: SaveBlocksRequest) =>
    request<SaveBlocksResponse>('PUT', '/api/rules/draft/blocks', body),
  /** «Отменить изменение» правила; последнее изменение удаляет черновик (`draft: null`). */
  revert: (body: { etag: string; group: string }) =>
    request<DraftResponse>('POST', '/api/rules/draft/revert', body),
  /** «Передать черновик» другому администратору правил объекта. */
  transfer: (body: { etag: string; toUserId: number }) =>
    request<DraftResponse>('POST', '/api/rules/draft/transfer', body),
  /** «Взять черновик», если у ведущего больше нет роли. */
  take: (body: { etag: string }) => request<DraftResponse>('POST', '/api/rules/draft/take', body),
  /** «Отменить черновик»: `204`. Администратор системы — только с причиной (`REASON_REQUIRED`). */
  discard: (etag: string, reason?: string) =>
    request<void>('DELETE', `/api/rules/draft${search({ etag })}`, reason ? { reason } : {}),
  /**
   * «Это изменение» и «Весь черновик» (contracts/impact.md). `token` — номер запроса интерфейса: новый запрос снимает
   * прежний (`409 IMPACT_SUPERSEDED`), ответ на старый номер интерфейс не показывает.
   */
  impact: (body: ImpactRequest) => request<ImpactResponse>('POST', '/api/rules/impact', body),
  /** Влияние всего черновика с даты начала: сохраняется на сервере (`previews`) для окна публикации. */
  preview: (body: { etag: string; validFrom: string }) =>
    request<RulesPreviewResponse>('POST', '/api/rules/draft/preview', body),
  /** Сохранённое влияние черновика: `computing`, `fresh`, `stale` или `failed`. */
  previewGet: (id: number) => request<RulesPreviewResponse>('GET', `/api/rules/draft/preview/${id}`),
  /*
   * Публикация (004, US3, T082; contracts/http-api.md, «Черновик и влияние»): «Так и должно быть» у проверочного
   * примера, «Понимаю риск» у предупреждения — отдельными запросами ведущего; ответ — новый `etag` черновика. Сама
   * публикация сверяет всё это на сервере ещё раз (data-model Г.4).
   */
  /** «Так и должно быть по новому правилу»: пример получит ожидаемое по черновику. */
  confirmGolden: (goldenId: number, etag: string) =>
    request<{ etag: string }>('POST', `/api/rules/draft/goldens/${goldenId}/confirm`, { etag }),
  /** Снять «Так и должно быть». */
  unconfirmGolden: (goldenId: number, etag: string) =>
    request<{ etag: string }>('DELETE', `/api/rules/draft/goldens/${goldenId}/confirm${search({ etag })}`),
  /** «Понимаю риск» с отпечатком риска: другой отпечаток — `409 RISK_CHANGED`. */
  ackRisk: (code: string, body: { etag: string; fingerprint: string }) =>
    request<{ etag: string }>('POST', `/api/rules/draft/risks/${encodeURIComponent(code)}/ack`, body),
  /** «Опубликовать»: версия и задача пересчёта (`jobId`; у будущей даты — `null`). Отказы — data-model Г.4. */
  publish: (body: PublishRequest) => request<PublishResponse>('POST', '/api/rules/draft/publish', body),
  /**
   * План изменения по ссылке (005, US5, T122; specs/005-rules-mcp/contracts/http-api.md,
   * `GET /api/rules/plans/:planId`): ответ разбирается схемой `PlanResponse` — иначе страница показала бы пустые
   * поля. Ответ не по схеме — сбой сервера с кодом ответа. Публикация по плану — `publish` с `planId`.
   */
  /** Планы объекта без ссылки (005, FR-065; `GET /api/rules/plans`): вкладка «Планы агентов», строка в полосе черновика. */
  rulesPlans: async (): Promise<PlanListResponse> => {
    const parsed = PlanListResponse.safeParse(await request<unknown>('GET', '/api/rules/plans'));
    if (!parsed.success) throw new ApiError(200, 'INTERNAL', 'Ответ сервера не по договору', null);
    return parsed.data;
  },
  rulesPlan: async (planId: number): Promise<PlanResponse> => {
    const parsed = PlanResponse.safeParse(await request<unknown>('GET', `/api/rules/plans/${planId}`));
    if (!parsed.success) throw new ApiError(200, 'INTERNAL', 'Ответ сервера не по договору', null);
    return parsed.data;
  },
  /** Скрыть сообщение о пересчёте табеля для себя (FR-042). */
  dismissRecalc: (timesheetId: number, jobId: number) =>
    request<void>('POST', `/api/timesheets/${timesheetId}/recalc-notice/dismiss`, { jobId }),
  /** «Оставить правку» (`keep`) или «Принять расчёт» (`accept`) по «правка устарела» (FR-044). */
  editDecision: (id: number, personId: string, date: string, decision: 'keep' | 'accept') =>
    request<unknown>('POST', `${cellUrl(id, personId, date)}/edit-decision`, { decision }),
  /**
   * Администраторы правил объекта для «Передать черновик». Адреса в contracts/http-api.md нет: его нужно добавить
   * (вопрос координатору в отчёте T066).
   */
  /*
   * Версии (004, US4, T089; contracts/http-api.md, «Версии»): «Вернуть эти правила» у версии «действовала» и «Не
   * вводить эти правила» у версии «начнёт действовать». Оба — только без черновика (`409 DRAFT_OPEN`), с причиной.
   */
  /** Влияние возврата к версии и прогон всех проверочных примеров; отказы — `DRAFT_OPEN`, `IMPL_GONE`, `NOT_FOUND`. */
  rollbackPreview: (versionId: number) =>
    request<Impact>('POST', `/api/rules/versions/${versionId}/rollback-preview`),
  /** «Вернуть»: новая версия — копия версии `versionId`, задача пересчёта. */
  rollback: (versionId: number, reason: string) =>
    request<RollbackResult>('POST', `/api/rules/versions/${versionId}/rollback`, { reason }),
  /** «Не вводить»: будущая версия не начнёт действовать, табели не меняются. */
  notIntroduce: (versionId: number, reason: string) =>
    request<{ version: VersionView }>('POST', `/api/rules/versions/${versionId}/not-introduce`, { reason }),
  /*
   * Проверочные примеры (004, US5, T095; contracts/http-api.md, «Проверочные примеры и просьбы»).
   */
  /** Примеры объекта; при черновике — у каждого `draft`: совпадает или изменится. */
  goldens: () => request<GoldenView[]>('GET', '/api/rules/goldens'),
  /** «Сделать проверочным примером»: копия входа дня, ожидаемое по действующей версии, псевдоним. */
  pinGolden: (body: PinGoldenRequest) => request<{ golden: GoldenView }>('POST', '/api/rules/goldens', body),
  /*
   * Просьбы о правилах (004, US6, T100): «Описать правило» и статус просьбы, который ставит администратор системы.
   */
  ruleRequests: () => request<RuleRequestView[]>('GET', '/api/rules/requests'),
  /** Проверка текста до отправки: ФИО или номер человека — предупреждение `personal_data`, отправить можно. */
  checkRuleRequest: (text: string) =>
    request<{ warnings: 'personal_data'[] }>('POST', '/api/rules/requests/check', { text }),
  /** «Отправить команде разработки»: просьба со статусом «Ждёт разбора». */
  createRuleRequest: (text: string) =>
    request<{ request: RuleRequestView; warnings: 'personal_data'[] }>('POST', '/api/rules/requests', {
      text,
    }),
  /** Статус просьбы: «В реестре» с номером Q-NN или «Отклонена» с причиной. */
  setRuleRequestStatus: (id: number, body: SetRuleRequestStatus) =>
    request<{ request: RuleRequestView }>('POST', `/api/rules/requests/${id}/status`, body),
};

/** Ответ «Вернуть»: новая версия и задача пересчёта (`RollbackResponse` общих схем). */
export interface RollbackResult {
  version: VersionView;
  jobId: number;
}

/** Дата «Правил на дату» и период табеля (`periodVersions`). */
export interface RulesQuery {
  date?: string;
  from?: string;
  to?: string;
}

/** Человек для «Выбрать день» (`GET /api/rules/people`). */
export interface RulePerson {
  personId: string;
  name: string;
}

/** Вопрос реестра расчёта (`GET /api/rules/registry`). */
export interface RegistryQuestion {
  code: string;
  title: string;
  source: 'file' | 'request';
}

/** Строка запроса из заданных значений, по порядку ключей; пустые не идут. */
function search(p: Readonly<Record<string, string | undefined>>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(p)) if (v) u.set(k, v);
  const s = u.toString();
  return s ? `?${s}` : '';
}

/**
 * Ключи запросов страницы «Правила расчёта». Роль — в ключе: от роли зависят черновик, область примера и ФИО;
 * объект в ключ не входит — при смене объекта кэш сбрасывает `RoleProvider`.
 */
export const rulesKeys = {
  all: ['rules'] as const,
  view: (role: AppRole, q: RulesQuery) =>
    ['rules', role, q.date ?? null, q.from ?? null, q.to ?? null] as const,
  example: (role: AppRole, group: string, date: string) => ['rules', role, 'example', group, date] as const,
  dayExplain: (role: AppRole, personId: string, date: string) =>
    ['rules', role, 'day-explain', personId, date] as const,
  history: (role: AppRole) => ['rules', role, 'history'] as const,
  registry: (role: AppRole) => ['rules', role, 'registry'] as const,
  /** «Сейчас / После публикации» дня: кандидат — в ключе строкой JSON. */
  day: (role: AppRole, personId: string, date: string, candidate: string) =>
    ['rules', role, 'day', personId, date, candidate] as const,
  people: (role: AppRole, query: string) => ['rules', role, 'people', query] as const,
  /** Влияние всего черновика: свежее, пока не изменились черновик (`etag`) и дата начала. */
  preview: (role: AppRole, etag: string, validFrom: string) =>
    ['rules', role, 'preview', etag, validFrom] as const,
  /**
   * Влияние в окне «Опубликовать»: своё, не общее с «Что изменится» — окно считает его заново при открытии, а после
   * `PREVIEW_STALE` — ещё раз (`round`).
   */
  publishPreview: (role: AppRole, etag: string, validFrom: string, round: number) =>
    ['rules', role, 'publish-preview', etag, validFrom, round] as const,
  /** Влияние возврата к версии: окно «Вернуть» считает его заново при каждом открытии. */
  rollbackPreview: (role: AppRole, versionId: number) =>
    ['rules', role, 'rollback-preview', versionId] as const,
  goldens: (role: AppRole) => ['rules', role, 'goldens'] as const,
  requests: (role: AppRole) => ['rules', role, 'requests'] as const,
  /** Планы агентов объекта (FR-065). */
  plans: (role: AppRole) => ['rules', role, 'plans'] as const,
  /** План изменения по ссылке (005): перечитывается вместе с правилами — ключ начинается с `rules`. */
  plan: (role: AppRole, planId: number) => ['rules', role, 'plan', planId] as const,
};

/** Адрес Excel «Скачать историю изменений» (research R19). */
export function historyXlsxUrl(p: { from?: string; to?: string } = {}): string {
  return `/api/rules/history.xlsx${search(p)}`;
}

/** Выгрузка Excel табеля (FR-029). */
export function downloadExport(id: number): Promise<void> {
  return downloadFile(`/api/timesheets/${id}/export.xlsx`, 'tabel.xlsx');
}

/** «Скачать историю изменений» правил объекта — файл Excel (004, FR-013). */
export function downloadHistoryXlsx(p: { from?: string; to?: string } = {}): Promise<void> {
  return downloadFile(historyXlsxUrl(p), 'история-правил.xlsx');
}

/** Скачать файл: нужны заголовки роли и объекта, поэтому через fetch и blob; имя — из `content-disposition`. */
async function downloadFile(url: string, fallbackName: string): Promise<void> {
  const res = await fetch(url, {
    credentials: 'include',
    headers: contextHeaders(),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new ApiError(
      res.status,
      data?.error?.code ?? 'INTERNAL',
      data?.error?.message ?? 'Ошибка выгрузки',
      data?.error?.requestId ?? res.headers.get('x-request-id'),
    );
  }
  const cd = res.headers.get('content-disposition') ?? '';
  const m = /filename\*=UTF-8''([^;]+)/.exec(cd);
  const name = m?.[1] ? decodeURIComponent(m[1]) : fallbackName;
  const href = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = href;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}
